import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Provider } from 'react-redux';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { store } from './store';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout/Layout';
import Login from './pages/Auth/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import Customers from './pages/Customers/Customers';
import CustomerDetails from './pages/Customers/CustomerDetails';
import AddCustomer from './pages/Customers/AddCustomer';
import EditCustomer from './pages/Customers/EditCustomer';
import Suppliers from './pages/Suppliers/Suppliers';
import SupplierDetails from './pages/Suppliers/SupplierDetails';
import StockManagement from './pages/Stock/Stock';
import AddSupplier from './pages/Suppliers/AddSupplier';
import EditSupplier from './pages/Suppliers/EditSupplier';
import Invoices from './pages/Invoices/Invoices';
import AddInvoice from './pages/Invoices/AddInvoice';
import EditInvoice from './pages/Invoices/EditInvoice';
import Orders from './pages/Orders/Orders';
import OrderDetail from './pages/Orders/OrderDetail';
import AddOrder from './pages/Orders/AddOrder';
import EditOrder from './pages/Orders/EditOrder';
import Subscriptions from './pages/Subscriptions/Subscriptions';
import AddSubscription from './pages/Subscriptions/AddSubscription';
import Stock from './pages/Stock/Stock';
import AddStock from './pages/Stock/AddStock';
import CategoriesManagement from './pages/Categories/Categories';
import CallCenter from './pages/CallCenter/CallCenter';
import Settings from './pages/Settings/Settings';
import UserManagement from './pages/Settings/UserManagement';
import RoleManagement from './pages/Settings/RoleManagement';
import ModuleManagement from './pages/Settings/ModuleManagement';
import Users from './pages/Users/Users';
import AddUser from './pages/Users/AddUser';
import EditUser from './pages/Users/EditUser';
import Roles from './pages/Roles/Roles';
import AddRole from './pages/Roles/AddRole';
import EditRole from './pages/Roles/EditRole';
import Modules from './pages/Modules/Modules';
import AddModule from './pages/Modules/AddModule';
import EditModule from './pages/Modules/EditModule';
import Profile from './pages/Profile/Profile';
import Locations from './pages/Locations/Locations';
import Teams from './pages/Teams/Teams';

const theme = createTheme({
  palette: {
    primary: {
      main: '#4A90E2',
      light: '#7BB3F0',
      dark: '#2E5BBA',
    },
    secondary: {
      main: '#50C878',
      light: '#7DD3A0',
      dark: '#3A9B5A',
    },
    background: {
      default: '#F8F9FA',
      paper: '#FFFFFF',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
  },
});

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
            <Route index element={<Dashboard />} />
            <Route path="teams" element={<Teams />} />
            <Route path="locations" element={<Locations />} />
            <Route path="customers" element={<Customers />} />
            <Route path="customers/add" element={<AddCustomer />} />
            <Route path="customers/:id" element={<CustomerDetails />} />
            <Route path="customers/:id/edit" element={<EditCustomer />} />
              <Route path="suppliers" element={<Suppliers />} />
              <Route path="suppliers/:id" element={<SupplierDetails />} />
              <Route path="stock" element={<StockManagement />} />
              <Route path="suppliers/add" element={<AddSupplier />} />
              <Route path="suppliers/:id/edit" element={<EditSupplier />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="invoices/add" element={<AddInvoice />} />
              <Route path="invoices/edit/:id" element={<EditInvoice />} />
              <Route path="orders" element={<Orders />} />
              <Route path="orders/:id" element={<OrderDetail />} />
              <Route path="orders/add" element={<AddOrder />} />
              <Route path="orders/add/:customerId" element={<AddOrder />} />
              <Route path="orders/edit/:id" element={<EditOrder />} />
              <Route path="subscriptions" element={<Subscriptions />} />
              <Route path="subscriptions/add" element={<AddSubscription />} />
              <Route path="stock" element={<Stock />} />
              <Route path="stock/add" element={<AddStock />} />
              <Route path="categories" element={<CategoriesManagement />} />
              <Route path="call-center" element={<CallCenter />} />
              <Route path="users" element={<Users />} />
              <Route path="users/add" element={<AddUser />} />
                <Route path="users/edit/:id" element={<EditUser />} />
                <Route path="roles" element={<Roles />} />
                <Route path="roles/add" element={<AddRole />} />
                <Route path="roles/edit/:id" element={<EditRole />} />
                <Route path="modules" element={<Modules />} />
                <Route path="modules/add" element={<AddModule />} />
                <Route path="modules/edit/:id" element={<EditModule />} />
                <Route path="profile" element={<Profile />} />
              <Route path="settings" element={<Settings />} />
            </Route>
          </Routes>
        </Router>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </ThemeProvider>
    </Provider>
  );
}

export default App;